<template>
  <div
    :class="['el-picker-panel el-date-picker', classes]">
    <div class="el-picker-panel__body-wrapper">
      <div class="el-picker-panel__body">
        <div
          class="el-date-picker__header"
          :class="{ 'el-date-picker__header--bordered': currentView === 'year' || currentView === 'month' }"
          v-show="currentView !== 'time'">
          <button
            type="button"
            @click="prevYear"
            :aria-label="t(`el.datepicker.prevYear`)"
            class="el-picker-panel__icon-btn el-date-picker__prev-btn el-icon-d-arrow-left"/>
          <button
            type="button"
            @click="prevMonth"
            v-show="currentView === 'date'"
            :aria-label="t(`el.datepicker.prevMonth`)"
            class="el-picker-panel__icon-btn el-date-picker__prev-btn el-icon-arrow-left"/>
          <span
            @click="showYearPicker"
            role="button"
            class="el-date-picker__header-label">{{ yearLabel }}
          </span>
          <span
            @click="showMonthPicker"
            v-show="currentView === 'date'"
            role="button"
            class="el-date-picker__header-label"
            :class="{ active: currentView === 'month' }">{{t(`el.datepicker.month${ month + 1 }`)}}
          </span>
          <button
            type="button"
            @click="nextYear"
            :aria-label="t(`el.datepicker.nextYear`)"
            class="el-picker-panel__icon-btn el-date-picker__next-btn el-icon-d-arrow-right"/>
          <button
            type="button"
            @click="nextMonth"
            v-show="currentView === 'date'"
            :aria-label="t(`el.datepicker.nextMonth`)"
            class="el-picker-panel__icon-btn el-date-picker__next-btn el-icon-arrow-right"/>
        </div>
        <div class="el-picker-panel__content">
          <date-table
            v-show="currentView === 'date'"
            selection-mode="range"
            :date="date"
            :default-value="null"
            :min-date="minDate"
            :max-date="maxDate"
            :range-state="rangeState"
            :disabled-date="disabledDate"
            :first-day-of-week="firstDayOfWeek"
            @changerange="handleChangeRange"
            @pick="handleRangePick"/>
          <year-table
            v-show="currentView === 'year'"
            @pick="handleYearPick"
            :default-value="defaultValue ? new Date(defaultValue) : null"
            :date="date"
            :disabled-date="disabledDate"/>
          <month-table
            v-show="currentView === 'month'"
            @pick="handleMonthPick"
            :default-value="defaultValue ? new Date(defaultValue) : null"
            :date="date"
            :disabled-date="disabledDate"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
import {
  modifyDate,
  modifyWithTimeString,
  prevYear,
  nextYear,
  prevMonth,
  nextMonth,
  changeYearMonthAndClampDate
} from 'element-ui/src/utils/date-util'
import Locale from 'element-ui/lib/mixins/locale'
import MonthTable from './month-table'
import DateTable from './date-table'
import YearTable from './year-table'

export default {
  mixins: [Locale],
  watch: {
    selectionMode (newVal) {
      if (newVal === 'month') {
        if (this.currentView !== 'year' || this.currentView !== 'month') {
          this.currentView = 'month'
        }
      } else if (newVal === 'dates') {
        this.currentView = 'date'
      }
    }
  },
  methods: {
    showMonthPicker () {
      this.currentView = 'month'
    },
    showYearPicker () {
      this.currentView = 'year'
    },
    prevMonth () {
      this.date = prevMonth(this.date)
    },
    nextMonth () {
      this.date = nextMonth(this.date)
    },
    prevYear () {
      if (this.currentView === 'year') {
        this.date = prevYear(this.date, 10)
      } else {
        this.date = prevYear(this.date)
      }
    },
    nextYear () {
      if (this.currentView === 'year') {
        this.date = nextYear(this.date, 10)
      } else {
        this.date = nextYear(this.date)
      }
    },
    handleMonthPick (month) {
      if (this.selectionMode === 'month') {
        this.date = modifyDate(this.date, this.year, month, 1)
      } else {
        this.date = changeYearMonthAndClampDate(this.date, this.year, month)
        this.currentView = 'date'
      }
    },
    handleRangePick (val) {
      const minDate = modifyWithTimeString(val.minDate)
      const maxDate = modifyWithTimeString(val.maxDate)

      if (this.maxDate === maxDate && this.minDate === minDate) {
        return
      }
      this.onPick && this.onPick(val)
      this.maxDate = maxDate
      this.minDate = minDate

      setTimeout(() => {
        this.maxDate = maxDate
        this.minDate = minDate
        this.$emit('pick', (this.maxDate && this.minDate) ? [this.maxDate, this.minDate] : '')
      }, 10)
    },
    handleYearPick (year) {
      if (this.selectionMode === 'year') {
        this.date = modifyDate(this.date, year, 0, 1)
      } else {
        this.date = changeYearMonthAndClampDate(this.date, year, this.month)
        this.currentView = 'month'
      }
    },
    getDefaultValue () {
      // if default-value is set, return it
      // otherwise, return now (the moment this method gets called)
      return this.defaultValue ? new Date(this.defaultValue) : new Date()
    },
    handleChangeRange (val) {
      this.minDate = val.minDate
      this.maxDate = val.maxDate
      this.rangeState = val.rangeState
    },
    // PUBLIC
    getValue () {
      return [this.minDate, this.maxDate]
    },
    reset () {
      this.date = this.getDefaultValue()
      this.selectionMode = 'day'
      this.currentView = 'date'
      this.minDate = ''
      this.maxDate = ''
      this.$emit('pick', '')
    }
  },
  components: {
    YearTable, MonthTable, DateTable
  },
  data () {
    return {
      classes: '',
      date: new Date(),
      defaultValue: null, // use getDefaultValue() for time computation
      defaultTime: null,
      selectionMode: 'day',
      shortcuts: '',
      currentView: 'date',
      disabledDate: '',
      firstDayOfWeek: 1,
      format: '',
      rangeState: {
        endDate: null,
        selecting: false,
        row: null,
        column: null
      },
      minDate: '',
      maxDate: ''
    }
  },
  computed: {
    year () {
      return this.date.getFullYear()
    },
    month () {
      return this.date.getMonth()
    },
    yearLabel () {
      const yearTranslation = this.t('el.datepicker.year')
      if (this.currentView === 'year') {
        const startYear = Math.floor(this.year / 10) * 10
        if (yearTranslation) {
          return startYear + ' ' + yearTranslation + ' - ' + (startYear + 9) + ' ' + yearTranslation
        }
        return startYear + ' - ' + (startYear + 9)
      }
      return this.year + ' ' + yearTranslation
    }
  }
}
</script>
